import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { Bell, Plus, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";

export function AnnouncementsTab({ classId, announcements, isTeacher, headers, onRefresh }) {
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", content: "" });

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      await axios.post(`${API}/announcements`, {
        class_id: classId,
        ...newAnnouncement
      }, { headers });
      toast.success("Announcement posted!");
      setNewAnnouncement({ title: "", content: "" });
      setAnnouncementDialog(false);
      onRefresh();
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const handleDeleteAnnouncement = async (announcementId, title) => {
    if (!confirm(`Delete announcement "${title}"?`)) return;
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, { headers });
      toast.success("Announcement deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <CardTitle className="font-['Manrope']">Announcements</CardTitle>
          <CardDescription>Important updates and notices</CardDescription>
        </div>
        {isTeacher && (
          <Dialog open={announcementDialog} onOpenChange={setAnnouncementDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="create-announcement-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Create Announcement</DialogTitle>
                <DialogDescription>Post an announcement to the class</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    data-testid="announcement-title-input"
                    placeholder="Announcement title"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Textarea
                    data-testid="announcement-content-input"
                    placeholder="Write your announcement..."
                    rows={4}
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                  />
                </div>
                <Button
                  onClick={handleCreateAnnouncement}
                  className="w-full rounded-full bg-primary text-primary-foreground"
                  data-testid="submit-announcement-btn"
                >
                  Post Announcement
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {announcements.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((ann) => (
              <motion.div
                key={ann.announcement_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 rounded-xl border border-border/50"
                data-testid={`announcement-${ann.announcement_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{ann.title}</h3>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(ann.created_at), "MMM d, h:mm a")}
                    </span>
                    {isTeacher && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteAnnouncement(ann.announcement_id, ann.title)}
                        data-testid={`delete-announcement-${ann.announcement_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-muted-foreground whitespace-pre-wrap">{ann.content}</p>
                <p className="text-sm text-muted-foreground mt-2">— {ann.teacher_name}</p>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
